import { Assignment as RmaIcon, ShoppingCart as OrderIcon, OpenInNew as OpenInNewIcon } from '@mui/icons-material';
import {
  Box,
  Typography,
  List,
  ListItem,
  ListItemText,
  Link as MuiLink,
  Divider,
  Chip,
  CircularProgress,
  Alert,
} from '@mui/material';
import PropTypes from 'prop-types';
import React from 'react';
import { useTranslation } from 'react-i18next';
import { Link as RouterLink } from 'react-router-dom';

import StatusChip from '@sophocles/components/StatusChip';

/**
 * UsageHistoryView - Display RMA and Order Sales usage history for a contact
 *
 * Contact Usage History UI
 *
 * Features:
 * - Display list of RMAs with clickable links
 * - Display list of Order Sales with clickable links
 * - Show total count
 * - Empty state if no usage found
 * - Loading and error states
 * - Format: "RMA-123 (Created: 2025-01-15)" → links to /rma/123/view
 */
const UsageHistoryView = ({ usageData, loading, error }) => {
  const { t } = useTranslation();

  // Loading state
  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', py: 4 }}>
        <CircularProgress size={32} />
        <Typography variant="body2" color="text.secondary" sx={{ ml: 2 }}>
          {t('contacts.usageHistory.loading', 'Loading usage history...')}
        </Typography>
      </Box>
    );
  }

  // Error state
  if (error) {
    return (
      <Box sx={{ py: 2 }}>
        <Alert severity="error">
          {t('contacts.usageHistory.error', 'Failed to load usage history. Please try again.')}
        </Alert>
      </Box>
    );
  }

  // No data
  if (!usageData) {
    return null;
  }

  const { rmas = [], order_sales = [], total_count = 0 } = usageData;
  const hasNoUsage = total_count === 0;

  // Empty state
  if (hasNoUsage) {
    return (
      <Box sx={{ py: 3, textAlign: 'center' }}>
        <Typography variant="body2" color="text.secondary">
          {t('contacts.usageHistory.noUsage', 'No usage history found for this contact.')}
        </Typography>
      </Box>
    );
  }

  // Format date helper
  const formatDate = dateString => {
    if (!dateString) return '';
    try {
      const date = new Date(dateString);
      return date.toLocaleDateString();
    } catch {
      return dateString;
    }
  };

  return (
    <Box sx={{ py: 2 }}>
      {/* Summary Header */}
      <Box sx={{ mb: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
        <Typography variant="subtitle2" color="text.secondary">
          {t('contacts.usageHistory.summary', 'Used in {{count}} RMA(s) and {{count2}} Order(s)', {
            count: rmas.length,
            count2: order_sales.length,
          })}
        </Typography>
        <Chip label={`${total_count} total`} size="small" color="primary" variant="outlined" />
      </Box>

      {/* RMAs Section */}
      {rmas.length > 0 && (
        <Box sx={{ mb: 3 }}>
          <Typography variant="subtitle2" sx={{ mb: 1, display: 'flex', alignItems: 'center', gap: 1 }}>
            <RmaIcon fontSize="small" color="action" />
            {t('contacts.usageHistory.rmas', 'RMAs')} ({rmas.length})
          </Typography>
          <List dense disablePadding>
            {rmas.map((rma, index) => (
              <React.Fragment key={rma.id}>
                <ListItem
                  disableGutters
                  sx={{
                    py: 0.5,
                    '&:hover': {
                      backgroundColor: 'action.hover',
                    },
                  }}
                >
                  <ListItemText
                    primary={
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <MuiLink
                          component={RouterLink}
                          to={rma.url || `/rma/${rma.id}/view`}
                          underline="hover"
                          color="primary"
                          sx={{ fontWeight: 500 }}
                        >
                          {rma.rma_number || `RMA-${rma.id}`}
                        </MuiLink>
                        <OpenInNewIcon fontSize="small" sx={{ opacity: 0.5 }} />
                      </Box>
                    }
                    secondary={
                      <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', flexWrap: 'wrap' }}>
                        {rma.created_at && (
                          <Typography variant="caption" color="text.secondary">
                            {t('contacts.usageHistory.created', 'Created')}: {formatDate(rma.created_at)}
                          </Typography>
                        )}
                        {rma.status && (
                          <StatusChip label={rma.status} color="neutral" sx={{ height: 18, fontSize: '0.65rem' }} />
                        )}
                      </Box>
                    }
                  />
                </ListItem>
                {index < rmas.length - 1 && <Divider component="li" />}
              </React.Fragment>
            ))}
          </List>
        </Box>
      )}

      {/* Order Sales Section */}
      {order_sales.length > 0 && (
        <Box>
          <Typography variant="subtitle2" sx={{ mb: 1, display: 'flex', alignItems: 'center', gap: 1 }}>
            <OrderIcon fontSize="small" color="action" />
            {t('contacts.usageHistory.orders', 'Orders')} ({order_sales.length})
          </Typography>
          <List dense disablePadding>
            {order_sales.map((order, index) => (
              <React.Fragment key={order.id}>
                <ListItem
                  disableGutters
                  sx={{
                    py: 0.5,
                    '&:hover': {
                      backgroundColor: 'action.hover',
                    },
                  }}
                >
                  <ListItemText
                    primary={
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <MuiLink
                          component={RouterLink}
                          to={order.url || `/orders/${order.id}`}
                          underline="hover"
                          color="primary"
                          sx={{ fontWeight: 500 }}
                        >
                          {order.order_number || `Order-${order.id}`}
                        </MuiLink>
                        <OpenInNewIcon fontSize="small" sx={{ opacity: 0.5 }} />
                      </Box>
                    }
                    secondary={
                      <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', flexWrap: 'wrap' }}>
                        {order.created_at && (
                          <Typography variant="caption" color="text.secondary">
                            {t('contacts.usageHistory.created', 'Created')}: {formatDate(order.created_at)}
                          </Typography>
                        )}
                        {order.status && (
                          <StatusChip label={order.status} color="neutral" sx={{ height: 18, fontSize: '0.65rem' }} />
                        )}
                      </Box>
                    }
                  />
                </ListItem>
                {index < order_sales.length - 1 && <Divider component="li" />}
              </React.Fragment>
            ))}
          </List>
        </Box>
      )}
    </Box>
  );
};

UsageHistoryView.propTypes = {
  usageData: PropTypes.shape({
    rmas: PropTypes.arrayOf(
      PropTypes.shape({
        id: PropTypes.number.isRequired,
        rma_number: PropTypes.string,
        created_at: PropTypes.string,
        status: PropTypes.string,
        url: PropTypes.string,
      }),
    ),
    order_sales: PropTypes.arrayOf(
      PropTypes.shape({
        id: PropTypes.number.isRequired,
        order_number: PropTypes.string,
        created_at: PropTypes.string,
        status: PropTypes.string,
        url: PropTypes.string,
      }),
    ),
    total_count: PropTypes.number,
  }),
  loading: PropTypes.bool,
  error: PropTypes.bool,
};

UsageHistoryView.defaultProps = {
  loading: false,
  error: false,
  usageData: null,
};

export default UsageHistoryView;
